import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, Col, Row, Form, Table } from 'react-bootstrap';

import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import TeachTrackingDetail from './TeachTrackingDetail';

const API = process.env.REACT_APP_API;

const subType = (t) => (t === 'extra' ? '(เพิ่มเติม)' : '');

// การ์ดรายวิชา 1 รายการ สำหรับมุมมองมือถือ
const SubjectRoomCard = ({ s, isOpen, onToggle }) => (
  <div className="border rounded p-3" style={{ cursor: 'pointer' }} onClick={onToggle}>
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">{s.sub_subid}</div>
      <i className={`ri-arrow-${isOpen ? 'down' : 'right'}-s-line`}></i>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div>{s.sub_subname} <span className="text-muted">{subType(s.sub_type)}</span></div>
      <div><span className="text-muted">ภาคเรียนที่:</span> {s.sub_semester || '-'}</div>
      <div><span className="text-muted">ครูผู้สอน:</span> {s.t_name || <span className="text-muted">-</span>}</div>
    </div>
  </div>
)

export default function AttendanceByRoom() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName('table');
    for (const t of tables) {
      const theads = t.querySelectorAll('thead');
      if (s === 'dark') theads.forEach((th) => th.classList.add('table-dark'));
      else theads.forEach((th) => th.classList.remove('table-dark'));
    }
  };
  useEffect(() => { switchSkin(skin); });

  const [yearId, setYearId] = useState(null);
  const [roomList, setRoomList] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [teachingRows, setTeachingRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');

  useEffect(() => {
    axiosInstance
      .get(`${API}/getLastestStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => { if (res.data?.year_id) setYearId(res.data.year_id); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance
      .get(`${API}/getRoomListByYear/${yearId}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => { if (res.data?.length) setRoomList(res.data); })
      .catch(() => {});
  }, [yearId]);

  useEffect(() => {
    if (!selectedRoom) { setTeachingRows([]); setLoaded(false); return; }
    setExpanded(null);
    setLoaded(false);
    axiosInstance
      .get(`${API}/getTeachingDataByRoomId/${selectedRoom}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => setTeachingRows(res.data || []))
      .catch(() => setTeachingRows([]))
      .finally(() => setLoaded(true));
  }, [selectedRoom]);

  // Dedupe teachtime rows into one entry per teaching assignment (teach_id)
  const subjects = useMemo(() => {
    const map = new Map();
    teachingRows.forEach((r) => {
      if (!r.teach_id || map.has(r.teach_id)) return;
      map.set(r.teach_id, {
        teach_id: r.teach_id,
        sub_subid: r.sub_subid,
        sub_subname: r.sub_subname,
        sub_type: r.sub_type,
        sub_semester: r.sub_semester,
        t_name: r.t_name,
      });
    });
    return Array.from(map.values()).sort(
      (a, b) => (a.sub_semester - b.sub_semester) || String(a.sub_subid).localeCompare(String(b.sub_subid))
    );
  }, [teachingRows]);

  const roomLabel = useMemo(() => {
    const room = roomList.find((r) => String(r.r_id) === String(selectedRoom));
    return room ? `ม.${room.c_class || room.c_id}/${room.r_no}${room.p_code ? ` (${room.p_code})` : ''}` : '';
  }, [roomList, selectedRoom]);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หัวหน้าวิชาการ</Link></li>
              <li className="breadcrumb-item active" aria-current="page">การเข้าเรียน/บันทึกการสอน รายห้องเรียน</li>
            </ol>
            <h4 className="main-title mb-0">การเข้าเรียน/บันทึกการสอน รายห้องเรียน</h4>
          </div>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col md={5}>
                <Form.Label className="fs-sm mb-1">เลือกห้องเรียน</Form.Label>
                <Form.Select value={selectedRoom} onChange={(e) => setSelectedRoom(e.target.value)}>
                  <option value="">-- เลือกห้องเรียน --</option>
                  {roomList.map((r) => (
                    <option key={r.r_id} value={r.r_id}>
                      ม.{r.c_class || r.c_id}/{r.r_no}{r.p_code ? ` (${r.p_code})` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {selectedRoom && (
          <Card className="card-one mb-4">
            <Card.Header className="d-flex flex-wrap align-items-center gap-2">
              <Card.Title as="h6" className="mb-0">
                รายวิชาในห้อง {roomLabel}
                <Badge bg="secondary" className="ms-2">{subjects.length} วิชา</Badge>
              </Card.Title>
              <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loaded && subjects.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="ri-book-2-line fs-3 d-block mb-2"></i>
                  ไม่มีรายวิชาที่จัดสอนในห้องนี้
                </div>
              ) : view === 'card' ? (
                <div className="p-3">
                  <Row className="g-3">
                    {subjects.map((s) => (
                      <Col key={s.teach_id} xs={12} md={6} xl={4}>
                        <SubjectRoomCard s={s} isOpen={expanded === s.teach_id}
                          onToggle={() => setExpanded(expanded === s.teach_id ? null : s.teach_id)} />
                      </Col>
                    ))}
                  </Row>
                  {expanded && (
                    <div className="mt-3 pt-3 border-top">
                      <TeachTrackingDetail teachId={expanded} />
                    </div>
                  )}
                </div>
              ) : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th style={{ width: 50 }}></th>
                      <th className="text-center" style={{ width: 60 }}>ภาค</th>
                      <th style={{ width: 110 }}>รหัสวิชา</th>
                      <th>รายวิชา</th>
                      <th>ครูผู้สอน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map((s) => (
                      <React.Fragment key={s.teach_id}>
                        <tr
                          style={{ cursor: 'pointer' }}
                          onClick={() => setExpanded(expanded === s.teach_id ? null : s.teach_id)}
                        >
                          <td className="text-center">
                            <i className={`ri-arrow-${expanded === s.teach_id ? 'down' : 'right'}-s-line`}></i>
                          </td>
                          <td className="text-center">{s.sub_semester || '-'}</td>
                          <td>{s.sub_subid}</td>
                          <td>{s.sub_subname} <span className="text-muted">{subType(s.sub_type)}</span></td>
                          <td>{s.t_name || <span className="text-muted">-</span>}</td>
                        </tr>
                        {expanded === s.teach_id && (
                          <tr>
                            <td colSpan={5} className="bg-body p-3">
                              <TeachTrackingDetail teachId={s.teach_id} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
